import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  CheckCircle2,
  Copy,
  Download,
  Mail,
  Share2,
  Home,
  Sparkles,
  Loader2,
} from "lucide-react";
import MembershipCardPreview from "@/components/MembershipCardPreview";
import { maskMobile } from "@/lib/membership";
import { downloadCardImage, cardFileName } from "@/lib/downloadCard";

export default function SuccessCard({ member, onReset }) {
  const cardRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const copyNumber = async () => {
    try {
      await navigator.clipboard.writeText(member.membershipNumber);
      setCopied(true);
      toast.success("Membership number copied");
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error("Couldn’t copy. Long-press to copy manually.");
    }
  };

  const downloadCard = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const filename = cardFileName(member.membershipNumber, member.name);
      await downloadCardImage(cardRef.current, filename);
      toast.success("Card downloaded");
    } catch (e) {
      toast.error("Download failed. Try again.");
    } finally {
      setDownloading(false);
    }
  };

  const sendWhatsApp = () => {
    toast("WhatsApp delivery in progress (mock)", {
      description: "Sent to " + maskMobile(member.mobile),
      icon: <MessageCircle className="h-4 w-4 text-success" />,
    });
  };

  return (
    <div className="animate-scale-in space-y-5">
      <Card className="border-success/30 bg-card shadow-soft overflow-hidden">
        <div className="relative bg-gradient-soft p-6 sm:p-8 border-b border-border">
          <div className="absolute inset-0 grid-pattern opacity-40" />
          <div className="relative flex items-start gap-4">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-success text-success-foreground shadow-elegant">
              <CheckCircle2 className="h-6 w-6" />
            </span>
            <div className="flex-1">
              <Badge className="bg-success/15 text-success border border-success/30 mb-2 hover:bg-success/15">
                Payment successful
              </Badge>
              <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                Welcome to 1Rupee Clinic, {member.name.split(" ")[0]}!
              </h1>
              <p className="mt-1.5 text-sm text-muted-foreground">
                Your membership is active till <span className="text-foreground font-medium">{member.expires}</span>.
              </p>
            </div>
          </div>
        </div>

        <CardContent className="p-6 sm:p-8">
          <div className="flex justify-center mb-6">
            <MembershipCardPreview
              innerRef={cardRef}
              name={member.name}
              mobile={member.mobile}
              membershipNumber={member.membershipNumber}
              issued={member.issued}
              expires={member.expires}
            />
          </div>

          <div className="rounded-xl border border-border bg-muted/40 p-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Membership number
              </div>
              <div className="font-mono-card text-lg font-semibold tracking-wider text-foreground truncate">
                {member.membershipNumber}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={copyNumber} className="shrink-0">
              <Copy className="h-4 w-4" /> {copied ? "Copied" : "Copy"}
            </Button>
          </div>

          <div className="mt-5">
            <Button onClick={downloadCard} variant="outline" className="h-11 rounded-xl w-full sm:w-auto" disabled={downloading}>
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {downloading ? "Saving…" : "Download card"}
            </Button>
          </div>

          <Separator className="my-6" />

          <div className="grid sm:grid-cols-3 gap-3 text-sm">
            <div className="rounded-xl border border-border bg-background p-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Cardholder</div>
              <div className="font-semibold text-foreground truncate">{member.name}</div>
            </div>
            <div className="rounded-xl border border-border bg-background p-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Mobile</div>
              <div className="font-semibold text-foreground truncate">{maskMobile(member.mobile)}</div>
            </div>
            <div className="rounded-xl border border-border bg-background p-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Payment ID</div>
              <div className="font-mono text-xs font-semibold text-foreground truncate">{member.paymentId}</div>
            </div>
          </div>

          <div className="mt-6 rounded-xl bg-primary/5 border border-primary/15 p-4 flex items-start gap-3">
            <Sparkles className="h-4 w-4 text-primary mt-0.5" />
            <div className="text-sm text-foreground">
              <span className="font-semibold">Pro tip:</span>{" "}
              <span className="text-muted-foreground">
                Save the card image to your phone’s gallery so you can show it at billing even without internet.
              </span>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Mail className="h-3.5 w-3.5" /> A receipt has been sent via SMS
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => toast("Share link copied (mock)")}>
                <Share2 className="h-4 w-4" /> Share
              </Button>
              <Link to="/">
                <Button variant="outline" size="sm" onClick={onReset}>
                  <Home className="h-4 w-4" /> Back to home
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
